import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAppHistoryState } from "./AppHistoryProvider";

export const useNavigator = () => {
  const history = useHistory();
  const location = useLocation();
  const { fallback, previous, current } = useAppHistoryState();

  // Generic
  const goBack = useCallback(() => {
    /* istanbul ignore else */
    if (previous && previous !== location.pathname) {
      // if we have captured a valid previous location, then a `goBack` should
      // in theory work by pushing the user back in the native browser stack.
      history.goBack();
    } else if (fallback) {
      // This block should really only execute on clicking the back button
      // when the user has landed on this page as the first page this session
      history.replace(fallback);
    } else {
      /* istanbul ignore next: this cannot be tested as the env is "test" in a test run */
      if (process.env.NODE_ENV === "development") {
        // Debug assist in dev mode only
        console.warn(
          `You have called "useNavigator :: goBack" without a valid fallback or previous location, this is probably a bug in your code.\n\rConsider using "useFallbackUrl(path)" to ensure a fallback is set on "${location.pathname}".`,
          "\n\nFor debugging purposes, the values that triggered this condition were:\n",
          "\nfallback",
          fallback,
          "\nprevious",
          previous,
          "\ncurrent",
          current,
        );
      }

      history.replace("/");
    }
  }, [current, fallback, history, location.pathname, previous]);

  // Dialogs & Modals
  const openOverlay = useCallback(history.push, [history]);
  const closeOverlay = goBack;
  const confirmOverlay = useCallback(history.replace, [history]);

  // Pages
  const gotoPage = useCallback(history.push, [history]);

  return {
    openOverlay,
    closeOverlay,
    confirmOverlay,
    gotoPage,
    goBack,
  };
};
